/**
 * Allowlist of Discovery funnel `event_key`s. Used to validate both public
 * entry points: the beacon route and `trackDiscoveryEvent` (the Server Action
 * can be invoked directly). Server Actions and RSCs call `sendDiscoveryEvent`
 * with the same keys.
 *
 * Comments on each key are the source of truth for what the event measures.
 * How the pipeline works: `docs/tracking-strategy.md`.
 */
export const DISCOVERY_EVENT_KEYS = [
	/** Discovery homepage entry. */
	"homepage_viewed",
	/** Visitor scrolled past the homepage program-list scroll-depth threshold. */
	"homepage_scrolled",
	/** Program page entry (`/program/[slug]`). */
	"program_page_viewed",
	/**
	 * Program link clicked (homepage tiles, header, footer, or program end
	 * card). Literal Mixpanel/GTM name — not Title Cased. Header and footer
	 * clicks from other apps send this same name from the browser.
	 */
	"aops__discovery__program_selected",
	/**
	 * Program group chosen within a program. Literal Mixpanel/GTM name.
	 * The group slug can differ from the course property `subject`.
	 */
	"aops__discovery__program_group_selected",
	/** Visitor scrolled past the program page's scroll-depth threshold. */
	"program_page_scrolled",
	/** Course-level page entry (`/course/[slug]`). */
	"course_page_viewed",
	/** Visitor scrolled past the course page's scroll-depth threshold. */
	"course_page_scrolled",
	/**
	 * Course Information panel opened, including when `?info=` opens it.
	 * Literal Mixpanel/GTM name.
	 */
	"aops__discovery__course_info_viewed",
	/**
	 * Format chosen — a real click or an auto-applied URL/session preference.
	 * Distinguish those with `modality_auto_select`. Literal Mixpanel/GTM name.
	 */
	"aops__discovery__modality_selected",
	/**
	 * "Learn more" opened on a modality, including when none is selected
	 * (`modality: "none"`). Literal Mixpanel/GTM name.
	 */
	"aops__discovery__modality_details_viewed",
	/**
	 * Course sequence / part chosen — a real click or an auto-applied
	 * preference. Distinguish those with `auto_select`. Literal Mixpanel/GTM name.
	 */
	"aops__discovery__sequence_selected",
	/** View Schedule CTA. Literal Mixpanel/GTM name. */
	"aops__discovery__schedule_requested",
	/**
	 * Book-consultation CTA (course page, or header/footer when the page has
	 * no course context). Literal Mixpanel/GTM name.
	 */
	"aops__discovery__consultation_request_initiated",
	/**
	 * Campus search cleared so the full campus list is showing again
	 * (clear button or "Show all campuses"). Literal Mixpanel/GTM name.
	 */
	"aops__discovery__location_reset",
	/**
	 * Campus search submitted (typed city/state/ZIP or current location),
	 * including a search that finds nothing or fails. Literal Mixpanel/GTM name.
	 */
	"aops__discovery__campus_searched",
	/** Course page link clicked from the cart. */
	"course_page_link_clicked",
	/** Header cart icon clicked (navigates to the cart page). */
	"cart_icon_clicked",
	/** Cart page loaded (`/cart`). */
	"cart_page_viewed",
	/** Product variant added to cart after a successful Shopify add. */
	"add_to_cart",
	/** Cart line removed after a successful Shopify remove. */
	"remove_from_cart",
	/** Checkout started after the cart passed validation. */
	"checkout_initiated",
	/** 2026 privacy-policy announcement modal became visible. */
	"privacy_policy_pop_up_viewed",
	/** Visitor clicked a review link from the privacy-policy announcement. */
	"opened_new_privacy_policy",
	/** Visitor clicked "I Agree" on the privacy-policy announcement. */
	"agreed_new_privacy_policy",
	/** Visitor left a Discovery privacy-policy page during the announcement flow. */
	"exited_privacy_policy_page",
	/**
	 * Summer program course card opened the course details modal.
	 * Carries campus_id, course_slug, session_number, and slot.
	 */
	"course_modal_opened",
	/**
	 * Summer program course details modal closed.
	 * Carries campus_id, course_slug, session_number, slot, time_open_ms, and scrolled.
	 */
	"course_modal_closed",
] as const;

export type DiscoveryEventKey = (typeof DISCOVERY_EVENT_KEYS)[number];

export function isDiscoveryEventKey(value: string): value is DiscoveryEventKey {
	return (DISCOVERY_EVENT_KEYS as readonly string[]).includes(value);
}

const MAX_PROPERTIES_KEYS = 20;
const MAX_PROPERTIES_BYTES = 10_000;

/**
 * Guards `properties` on both public entry points. Without this, `properties`
 * flows from an unauthenticated request body into Mixpanel/GTM: a string or
 * array would spread into numeric-keyed junk properties, with no size cap.
 */
export function isValidPropertiesBag(value: unknown): boolean {
	if (value === undefined) {
		return true;
	}
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	if (Object.keys(value).length > MAX_PROPERTIES_KEYS) {
		return false;
	}
	try {
		if (JSON.stringify(value).length > MAX_PROPERTIES_BYTES) {
			return false;
		}
	} catch {
		return false;
	}
	return true;
}

/**
 * Guards `referrer` on both public entry points, same reasoning as
 * `isValidPropertiesBag` above.
 */
export function isValidReferrer(value: unknown): boolean {
	return value === undefined || value === null || typeof value === "string";
}

const MAX_SCREEN_DIMENSION = 100_000;

/** A browser `window.screen` dimension. Absent or out of range is not a dimension. */
export function screenDimension(value: unknown): number | undefined {
	if (
		typeof value === "number" &&
		Number.isFinite(value) &&
		value >= 0 &&
		value <= MAX_SCREEN_DIMENSION
	) {
		return value;
	}
	return undefined;
}

const GTM_SERVER_PREVIEW_HEADER_PATTERN = /^[A-Za-z0-9+/]+=*$/;
const GTM_SERVER_PREVIEW_HEADER_MAX_LENGTH = 512;

/**
 * Shape-checks `x-gtm-server-preview` before forwarding to Stape. Optional
 * debug metadata: invalid values are treated as absent, not a hard reject.
 */
export function isValidGtmServerPreviewHeader(value: string): boolean {
	return (
		value.length > 0 &&
		value.length <= GTM_SERVER_PREVIEW_HEADER_MAX_LENGTH &&
		GTM_SERVER_PREVIEW_HEADER_PATTERN.test(value)
	);
}

/**
 * Converts a snake_case event key into the Title Case display name Mixpanel
 * expects as the actual event name (per the Discovery event spec: `event_key`
 * is a data-warehouse-friendly property, not the tracked event name itself).
 */
export function formatDiscoveryEventDisplayName(eventKey: string): string {
	return eventKey
		.split("_")
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

/**
 * New `app__surface__action` names are sent to Mixpanel and GTM as-is.
 * Older keys are still Title Cased.
 */
export function discoveryEventTrackingName(eventKey: string): string {
	return eventKey.includes("__")
		? eventKey
		: formatDiscoveryEventDisplayName(eventKey);
}

const STAFF_EMAIL_DOMAINS = new Set([
	"artofproblemsolving.com",
	"aops.com",
	"aopsacademy.org",
]);

/**
 * Interim `is_staff` email rule: an AoPS email domain. Office IPs are OR'd
 * in at send time and are not part of this check.
 */
export function isStaffEmail(email: string | null | undefined): boolean {
	if (!email) {
		return false;
	}
	const at = email.lastIndexOf("@");
	if (at < 0 || at === email.length - 1) {
		return false;
	}
	return STAFF_EMAIL_DOMAINS.has(email.slice(at + 1).toLowerCase());
}

/** Mixpanel `page` values. Anything else is omitted. */
export type DiscoveryPage =
	"home" | "course" | "program" | "about" | "checkout";

// Single-segment app routes that are not exploratory CMS pages (`about`).
const NON_CONTENT_SLUGS = new Set([
	"api",
	"cart",
	"catalog",
	"checkout",
	"course",
	"fallback-login",
	"llms.txt",
	"login",
	"logout",
	"md",
	"program",
	"shopify-poc",
	"sitemap.xml",
]);

/**
 * Mixpanel `page` for the current Discovery URL. Strips the Discovery base
 * path (`/discover`) when present.
 *
 * - home: landing (`/`)
 * - course: course detail pages (`/course/[slug]`)
 * - program: a program's course subset (`/program/[slug]`)
 * - about: other exploratory content (about, tuition, and other CMS slugs)
 * - checkout: cart flow (`/cart` and `/checkout`, including nested steps)
 *
 * Unknown paths are omitted.
 */
export function discoveryPageFromPathname(
	pathname: string | null,
	basePath = process.env.NEXT_PUBLIC_DISCOVERY_BASE_PATH ?? ""
): DiscoveryPage | undefined {
	if (!pathname) {
		return undefined;
	}
	let path = pathname;
	const normalizedBase = basePath.endsWith("/")
		? basePath.slice(0, -1)
		: basePath;
	if (
		normalizedBase &&
		(path === normalizedBase || path.startsWith(`${normalizedBase}/`))
	) {
		path = path.slice(normalizedBase.length) || "/";
	}
	if (path.length > 1 && path.endsWith("/")) {
		path = path.slice(0, -1);
	}
	if (path === "" || path === "/") {
		return "home";
	}
	if (/^\/course\/[^/]+$/.test(path)) {
		return "course";
	}
	if (/^\/program\/[^/]+$/.test(path)) {
		return "program";
	}
	if (path === "/cart" || path.startsWith("/cart/")) {
		return "checkout";
	}
	if (path === "/checkout" || path.startsWith("/checkout/")) {
		return "checkout";
	}
	const segments = path.split("/").filter(Boolean);
	const slug = segments[0];
	if (
		segments.length === 1 &&
		slug &&
		!slug.includes(".") &&
		!NON_CONTENT_SLUGS.has(slug)
	) {
		return "about";
	}
	return undefined;
}
